import type { ConnectionView, CreatePostInput, PostView } from "@cp/core";

/** Empty when the API is same-origin (Worker serves the SPA). Set VITE_API_BASE
 * to an absolute origin (e.g. https://api.example.com) for a split deployment. */
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

let activeOrgId: string | null = null;
export function setActiveOrg(id: string | null) {
  activeOrgId = id;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  if (activeOrgId) headers.set("X-Org-Id", activeOrgId);
  const res = await fetch(`${API_BASE}/api${path}`, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; problems?: unknown };
    throw new Error(body.error ?? `HTTP ${res.status}`, { cause: body.problems });
  }
  return res.json() as Promise<T>;
}

export interface MeResponse {
  user: { id: string; email: string; name: string };
  activeOrgId: string | null;
  organizations: Array<{ id: string; name: string; slug: string | null; role: string }>;
}

export const api = {
  me: () => req<MeResponse>("/me"),
  connections: () => req<{ items: ConnectionView[] }>("/connections"),
  deleteConnection: (id: string) => req<{ ok: true }>(`/connections/${id}`, { method: "DELETE" }),
  oauthStartUrl: (platform: string) =>
    `${API_BASE}/api/oauth/${platform}/start?return=${encodeURIComponent(window.location.origin)}`,

  media: () => req<{ items: Array<{ id: string; kind: string; url: string | null; status: string }> }>("/media"),
  createMedia: (body: { filename: string; mime: string; bytes: number; kind: "image" | "video" }) =>
    req<{ id: string; key: string; uploadUrl: string }>("/media", { method: "POST", body: JSON.stringify(body) }),
  completeMedia: (id: string, body: Record<string, unknown>) =>
    req<{ id: string; status: string; previewUrl: string }>(`/media/${id}/complete`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  posts: () => req<{ items: Array<{ id: string; caption: string; status: string; scheduledAt: string | null }> }>("/posts"),
  post: (id: string) => req<PostView>(`/posts/${id}`),
  createPost: (body: CreatePostInput, publishNow = false) =>
    req<PostView>(`/posts${publishNow ? "?publishNow=1" : ""}`, { method: "POST", body: JSON.stringify(body) }),
  cancelPost: (id: string) => req<{ ok: true }>(`/posts/${id}/cancel`, { method: "POST" }),
  retryTarget: (postId: string, targetId: string) =>
    req<{ ok: true }>(`/posts/${postId}/targets/${targetId}/retry`, { method: "POST" }),
};
